import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import type { Tensor3D } from '@tensorflow/tfjs-core';
import type { BlazeFaceModel } from '@tensorflow-models/blazeface';
import { CameraFocus, ImageQualityMetrics } from '../types';
import { evaluateImageQuality } from '../services/imageReportService';
import './ImageCapture.css';

export interface ImageCaptureProps {
  focus: CameraFocus;
  file: File | null;
  previewUrl: string | null;
  onClear: () => void;
  onQualityUpdate?: (quality: ImageQualityMetrics | null) => void;
  autoCheck?: boolean;
}

type ClientMetrics = {
  clarity: number;
  lighting: number;
  composition: number;
  faceConfidence: number;
  centerOffset: number;
};

type HudSeverity = 'good' | 'warning' | 'alert';

const QUALITY_LABEL_TEXT: Record<string, string> = {
  excellent: '质量优秀',
  acceptable: '质量可用',
  needs_retake: '建议重拍',
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function estimateClarity(imageData: ImageData): number {
  const { data, width, height } = imageData;
  let total = 0;
  let count = 0;
  for (let y = 1; y < height; y += 2) {
    for (let x = 1; x < width; x += 2) {
      const idx = (y * width + x) * 4;
      const gray = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
      const leftIdx = (y * width + (x - 1)) * 4;
      const topIdx = ((y - 1) * width + x) * 4;
      const leftGray = data[leftIdx] * 0.299 + data[leftIdx + 1] * 0.587 + data[leftIdx + 2] * 0.114;
      const topGray = data[topIdx] * 0.299 + data[topIdx + 1] * 0.587 + data[topIdx + 2] * 0.114;
      total += Math.abs(gray - leftGray) + Math.abs(gray - topGray);
      count += 1;
    }
  }
  const average = count === 0 ? 0 : total / count;
  return clamp(average / 30, 0, 1);
}

function estimateLighting(imageData: ImageData): number {
  const { data } = imageData;
  let total = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    const brightness = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    total += brightness;
  }
  const avg = pixels === 0 ? 0 : total / pixels;
  return clamp(avg / 255, 0, 1);
}

function estimateComposition(imageData: ImageData): number {
  const { data, width, height } = imageData;
  const centerX1 = Math.floor(width * 0.3);
  const centerX2 = Math.floor(width * 0.7);
  const centerY1 = Math.floor(height * 0.3);
  const centerY2 = Math.floor(height * 0.7);

  let centerIntensity = 0;
  let totalIntensity = 0;

  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const idx = (y * width + x) * 4;
      const gray = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
      totalIntensity += gray;
      if (x >= centerX1 && x <= centerX2 && y >= centerY1 && y <= centerY2) {
        centerIntensity += gray;
      }
    }
  }

  if (totalIntensity === 0) {
    return 0;
  }

  const ratio = centerIntensity / totalIntensity;
  return clamp((ratio - 0.2) / 0.6, 0, 1);
}

type FaceDetectionInput = HTMLImageElement | HTMLCanvasElement | HTMLVideoElement | ImageData;

let cachedTfModule: typeof import('@tensorflow/tfjs') | null = null;
let cachedModel: BlazeFaceModel | null = null;

async function ensureFaceModel(): Promise<{ tf: typeof import('@tensorflow/tfjs'); model: BlazeFaceModel }> {
  if (!cachedTfModule) {
    const tfModule = await import('@tensorflow/tfjs');
    await tfModule.ready();
    cachedTfModule = tfModule;
  }
  if (!cachedModel) {
    const blazeface = await import('@tensorflow-models/blazeface');
    cachedModel = await blazeface.load({ maxFaces: 1 });
  }
  return { tf: cachedTfModule, model: cachedModel };
}

function isHtmlMediaElement(input: FaceDetectionInput): input is HTMLCanvasElement | HTMLImageElement | HTMLVideoElement {
  return input instanceof HTMLCanvasElement || input instanceof HTMLImageElement || input instanceof HTMLVideoElement;
}

type FaceDetectionMetrics = {
  confidence: number;
  compositionScore: number;
  centerOffset: number;
};

const DEFAULT_FACE_METRICS: FaceDetectionMetrics = {
  confidence: 0,
  compositionScore: 0,
  centerOffset: 1,
};

async function detectFaceMetrics(source: FaceDetectionInput, flipHorizontal = false): Promise<FaceDetectionMetrics> {
  try {
    const { tf, model } = await ensureFaceModel();
    let tensorInput: Tensor3D | null = null;
    let estimationTarget: FaceDetectionInput | Tensor3D = source;

    if (!isHtmlMediaElement(source)) {
      tensorInput = tf.browser.fromPixels(source as ImageData) as unknown as Tensor3D;
      estimationTarget = tensorInput;
    }

    const rawResults = await model.estimateFaces(estimationTarget as HTMLImageElement, false, flipHorizontal);
    tensorInput?.dispose();

    const predictions = (rawResults || []) as Array<{
      probability?: number | number[];
      topLeft?: [number, number] | { x: number; y: number };
      bottomRight?: [number, number] | { x: number; y: number };
    }>;

    if (!predictions || predictions.length === 0) {
      return DEFAULT_FACE_METRICS;
    }

    const best = predictions.reduce((prev, curr) => {
      const prevScore = Array.isArray(prev.probability) ? prev.probability[0] ?? 0 : prev.probability ?? 0;
      const currScore = Array.isArray(curr.probability) ? curr.probability[0] ?? 0 : curr.probability ?? 0;
      return currScore > prevScore ? curr : prev;
    }, predictions[0]);

    const score = clamp(Array.isArray(best.probability) ? best.probability[0] ?? 0 : best.probability ?? 0, 0, 1);

    const extractPoint = (
      value?: [number, number] | { x: number; y: number }
    ): [number, number] | null => {
      if (!value) {
        return null;
      }
      if (Array.isArray(value)) {
        return [value[0], value[1]];
      }
      if (typeof value.x === 'number' && typeof value.y === 'number') {
        return [value.x, value.y];
      }
      return null;
    };

    const topLeft = extractPoint(best.topLeft);
    const bottomRight = extractPoint(best.bottomRight);

    if (!topLeft || !bottomRight) {
      return {
        confidence: score,
        compositionScore: 0,
        centerOffset: 1,
      };
    }

    const [left, top] = topLeft;
    const [right, bottom] = bottomRight;

    let imageWidth = 0;
    let imageHeight = 0;
    if (source instanceof HTMLImageElement) {
      imageWidth = source.naturalWidth || source.width;
      imageHeight = source.naturalHeight || source.height;
    } else if (source instanceof HTMLCanvasElement) {
      imageWidth = source.width;
      imageHeight = source.height;
    } else if (source instanceof HTMLVideoElement) {
      imageWidth = source.videoWidth;
      imageHeight = source.videoHeight;
    } else {
      imageWidth = (source as ImageData).width;
      imageHeight = (source as ImageData).height;
    }

    if (!imageWidth || !imageHeight) {
      return {
        confidence: score,
        compositionScore: 0,
        centerOffset: 1,
      };
    }

    const faceCenterX = left + (right - left) / 2;
    const faceCenterY = top + (bottom - top) / 2;
    const normalizedX = faceCenterX / imageWidth;
    const normalizedY = faceCenterY / imageHeight;
    const offsetX = Math.abs(normalizedX - 0.5) * 2;
    const offsetY = Math.abs(normalizedY - 0.5) * 2;
    const weightedOffset = clamp(offsetX * 0.65 + offsetY * 0.35, 0, 1);
    const compositionScore = clamp(1 - weightedOffset, 0, 1);

    return {
      confidence: score,
      compositionScore,
      centerOffset: weightedOffset,
    };
  } catch (error) {
    console.warn('[ImageCapture] face detection failed', error);
    return DEFAULT_FACE_METRICS;
  }
}

const ImageCapture: React.FC<ImageCaptureProps> = ({
  focus,
  file,
  previewUrl,
  onClear,
  onQualityUpdate,
  autoCheck = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [quality, setQuality] = useState<ImageQualityMetrics | null>(null);
  const [clientMetrics, setClientMetrics] = useState<ClientMetrics | null>(null);
  const [hudMessage, setHudMessage] = useState<string | null>(null);
  const [loadingQuality, setLoadingQuality] = useState<boolean>(false);
  const [qualityError, setQualityError] = useState<string | null>(null);
  const [lastCheckedFileId, setLastCheckedFileId] = useState<string>('');

  useEffect(() => {
    if (!previewUrl) {
      setClientMetrics(null);
      setQuality(null);
      setHudMessage(null);
      setQualityError(null);
      onQualityUpdate?.(null);
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = async () => {
      if (cancelled) {
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const width = 320;
      const height = Math.floor((image.height / image.width) * width) || 240;
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(image, 0, 0, width, height);
      const imageData = ctx.getImageData(0, 0, width, height);

      const clarity = estimateClarity(imageData);
      const lighting = estimateLighting(imageData);
      const compositionFallback = estimateComposition(imageData);
      const faceMetrics = await detectFaceMetrics(image);

      if (cancelled) {
        return;
      }

      const compositionScore = faceMetrics.compositionScore > 0 ? faceMetrics.compositionScore : compositionFallback;

      setClientMetrics({
        clarity,
        lighting,
        composition: compositionScore,
        faceConfidence: faceMetrics.confidence,
        centerOffset: faceMetrics.centerOffset,
      });

      if (faceMetrics.confidence < 0.2) {
        setHudMessage('未检测到清晰的面部主体，请调整姿势或构图');
      } else if (faceMetrics.centerOffset > 0.5) {
        setHudMessage('主体不居中，建议重新调整角度');
      } else if (clarity < 0.25) {
        setHudMessage('画面略显模糊，请保持稳定');
      } else {
        setHudMessage(null);
      }
    };

    image.onerror = () => {
      if (!cancelled) {
        setClientMetrics(null);
        setHudMessage('预览加载失败，请重新选择图片');
      }
    };

    image.src = previewUrl;

    return () => {
      cancelled = true;
    };
  }, [previewUrl, onQualityUpdate]);

  useEffect(() => {
    if (!file || !autoCheck) {
      setQuality(null);
      setQualityError(null);
      onQualityUpdate?.(null);
      return;
    }

    const signature = `${file.name}-${file.size}-${file.lastModified}`;
    if (signature === lastCheckedFileId) {
      return;
    }

    let cancelled = false;
    setLoadingQuality(true);
    setQualityError(null);

    evaluateImageQuality(file, focus)
      .then((response) => {
        if (cancelled) {
          return;
        }
        setQuality(response.quality);
        setHudMessage(response.quality.issues[0] ?? null);
        setLastCheckedFileId(signature);
        onQualityUpdate?.(response.quality);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error('[ImageCapture] quality check failed', error);
        setQuality(null);
        setQualityError('质控服务暂时不可用，请稍后重试。');
        onQualityUpdate?.(null);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingQuality(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [file, focus, autoCheck, lastCheckedFileId, onQualityUpdate]);

  const qualitySeverity: HudSeverity = useMemo(() => {
    if (!quality) {
      return 'warning';
    }
    if (quality.quality_label === 'excellent') {
      return 'good';
    }
    if (quality.quality_label === 'acceptable') {
      return 'warning';
    }
    return 'alert';
  }, [quality]);

  return (
    <div className="image-capture-card">
      <canvas ref={canvasRef} className="image-capture-hidden-canvas" aria-hidden="true" />
      <div className={classNames('image-capture-preview', qualitySeverity)}>
        {previewUrl ? (
          <img src={previewUrl} alt="影像预览" />
        ) : (
          <div className="image-capture-empty">
            <span className="icon">📷</span>
            <p>等待影像输入</p>
          </div>
        )}
        {hudMessage && (
          <div className="image-capture-hud">
            <span className="hud-text">{hudMessage}</span>
          </div>
        )}
        {file && (
          <button type="button" className="image-capture-clear" onClick={onClear} aria-label="清除影像">
            ×
          </button>
        )}
      </div>

      <div className="image-capture-metrics">
        <header className="metrics-header">
          <h4>实时质控</h4>
          {quality && <span className={`quality-label ${qualitySeverity}`}>{QUALITY_LABEL_TEXT[quality.quality_label]}</span>}
        </header>

        <div className="metrics-grid">
          <MetricBar
            label="清晰度"
            value={(clientMetrics?.clarity ?? 0) * 100}
            description="越高越清晰"
            alertThreshold={20}
            warningThreshold={35}
          />
          <MetricBar
            label="光照"
            value={(clientMetrics?.lighting ?? 0) * 100}
            description="适中最优"
            alertThreshold={15}
            warningThreshold={28}
          />
          <MetricBar
            label="构图"
            value={(clientMetrics?.composition ?? 0) * 100}
            description="主体居中"
            alertThreshold={40}
            warningThreshold={65}
          />
          <MetricBar
            label="面部定位"
            value={(clientMetrics?.faceConfidence ?? 0) * 100}
            description="模型识别置信度"
            alertThreshold={20}
            warningThreshold={45}
          />
        </div>

        <div className="quality-summary">
          {loadingQuality && <span className="loading">正在调用AI质控…</span>}
          {!loadingQuality && quality && (
            <>
              <p className="suggestion">{quality.suggestion}</p>
              {quality.issues.length > 0 && (
                <ul className="issues">
                  {quality.issues.map((issue) => (
                    <li key={issue}>{issue}</li>
                  ))}
                </ul>
              )}
            </>
          )}
          {!loadingQuality && !quality && !qualityError && (
            <p className="hint">上传影像后将自动进行质控评估。</p>
          )}
          {qualityError && <p className="error">{qualityError}</p>}
        </div>
      </div>
    </div>
  );
};

interface MetricBarProps {
  label: string;
  value: number;
  description?: string;
  alertThreshold?: number;
  warningThreshold?: number;
}

const MetricBar: React.FC<MetricBarProps> = ({
  label,
  value,
  description,
  alertThreshold = 35,
  warningThreshold = 65,
}) => {
  const normalized = clamp(value, 0, 100);

  let tone: HudSeverity = 'good';
  if (normalized < alertThreshold) {
    tone = 'alert';
  } else if (normalized < warningThreshold) {
    tone = 'warning';
  }

  return (
    <div className="metric-bar">
      <div className="metric-head">
        <span>{label}</span>
        <strong>{Math.round(normalized)}</strong>
      </div>
      <div className={classNames('metric-track', tone)}>
        <div className="metric-progress" style={{ width: `${normalized}%` }} />
      </div>
      {description && <span className="metric-desc">{description}</span>}
    </div>
  );
};

export default ImageCapture;
